import { BuyBackPurchaseOrderForm } from "@/components/operations/BuyBackPurchaseOrderForm";
import { SourceDealActions } from "@/components/operations/SourceDealActions";
import { WorkflowSummaryCard } from "@/components/operations/WorkflowSummaryCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  useCancelPurchaseOrder,
  useInventoryById,
  useInvoicesByPoId,
  usePaymentsByTarget,
  usePaymentsByTargets,
  usePurchaseOrderById,
  useUnlinkPurchaseOrder,
  useUpdatePaymentStatus,
  useUpdatePurchaseOrder,
  useUsers,
} from "@/hooks";
import { formatDateOnly, formatTimestamp } from "@/utils/formatting";
import { getPurchaseOrderTotal } from "@/utils/order-total";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  buildInventoryWorkspacePath,
  formatCurrency,
  formatNumber,
  formatStatusLabel,
  getClearedInvoiceAmountsById,
  getPurchaseOrderAttentionDetails,
  getPurchaseOrderDisplayStatus,
} from "./operations-shared";

interface PurchaseOrderSheetProps {
  poId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PurchaseOrderSheet(props: Readonly<PurchaseOrderSheetProps>) {
  const { poId, open, onOpenChange } = props;
  const purchaseOrderQuery = usePurchaseOrderById(poId);
  const updatePurchaseOrderMutation = useUpdatePurchaseOrder();
  const unlinkPurchaseOrderMutation = useUnlinkPurchaseOrder();
  const cancelPurchaseOrderMutation = useCancelPurchaseOrder();
  const approvePaymentMutation = useUpdatePaymentStatus();
  const purchaseOrder = purchaseOrderQuery.data;
  const inventoryQuery = useInventoryById(purchaseOrder?.inventoryId || "");
  const usersQuery = useUsers();
  const users = usersQuery.data ?? [];
  const paymentsQuery = usePaymentsByTarget({
    appliesToType: "po",
    appliesToId: purchaseOrder?.id || "",
  });

  const accountNeedsAction = Boolean(
    inventoryQuery.data &&
      (inventoryQuery.data.accountStatus === "needs_reset" ||
        !inventoryQuery.data.accountEmail ||
        !inventoryQuery.data.accountPassword),
  );
  // Invoices belonging to this PO, plus the payments cleared against them.
  // Money paid to the vendor usually flows through a purchase invoice, so the
  // cleared total must include invoice payments — not just payments applied
  // directly to the PO.
  const poInvoicesQuery = useInvoicesByPoId(purchaseOrder?.id || "");
  const poInvoiceIds = (poInvoicesQuery.data || []).map((invoice) => invoice.id);
  const invoicePaymentsQuery = usePaymentsByTargets({
    appliesToType: "invoice",
    appliesToIds: poInvoiceIds,
  });
  const clearedDirectAmount = (paymentsQuery.data || [])
    .filter(
      (payment) =>
        payment.status === "cleared" &&
        payment.direction !== "incoming" &&
        // Sourced payments (PayPal sync) are context-only, not counted.
        !payment.source,
    )
    .reduce((sum, payment) => sum + payment.amount, 0);
  const clearedInvoiceAmount = Array.from(
    getClearedInvoiceAmountsById({
      payments: invoicePaymentsQuery.data || [],
    }).values(),
  ).reduce((sum, amount) => sum + amount, 0);
  const clearedAmount = clearedDirectAmount + clearedInvoiceAmount;
  const totalAmount = purchaseOrder
    ? getPurchaseOrderTotal({
        buyPrice: purchaseOrder.buyPrice,
        taxCost: purchaseOrder.taxCost,
        totalAmount: purchaseOrder.totalAmount,
      })
    : 0;
  const remainingAmount = purchaseOrder
    ? Math.max(totalAmount - clearedAmount, 0)
    : 0;
  const health = purchaseOrder
    ? getPurchaseOrderAttentionDetails({
        status: purchaseOrder.status,
        paymentStatus: purchaseOrder.paymentStatus,
        voidType: purchaseOrder.voidType,
        accountNeedsAction,
      })
    : undefined;
  const isVoided = purchaseOrder?.status === "voided";
  // POs voided before `voidType` existed were all buybacks.
  const isCancelled = isVoided && purchaseOrder?.voidType === "cancelled";
  const alreadyReturned = purchaseOrder?.milesReturned ?? 0;
  const alreadyRefunded = purchaseOrder?.refundAmount ?? 0;
  // The ledger has to be in before `clearedAmount` means anything — otherwise an
  // in-flight load reads as "nothing paid" and we'd offer a cancel the server refuses.
  const isLedgerLoaded =
    !paymentsQuery.isLoading &&
    !poInvoicesQuery.isLoading &&
    !invoicePaymentsQuery.isLoading;
  // Cancelling is the "no money moved" path; once anything has cleared toward the
  // vendor the refund has to go through a buyback so it stays on the ledger. The
  // server enforces this too — this only keeps the UI honest about which path applies.
  const canCancel = !isVoided && isLedgerLoaded && clearedAmount <= 0;

  const [status, setStatus] = useState<"open" | "closed" | "blocked">("open");
  const [paymentTiming, setPaymentTiming] = useState<
    "pay_in_advance" | "pay_after" | "pay_once_booked"
  >("pay_in_advance");
  const [buyPrice, setBuyPrice] = useState("");
  const [taxCost, setTaxCost] = useState("");
  const [nextPaymentDate, setNextPaymentDate] = useState("");
  const [okToPay, setOkToPay] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<
    | "paypal_goods_services"
    | "paypal_friends_family"
    | "paypal_goods_services_fees"
    | "paypal"
    | "bank"
    | "zelle"
    | "card"
    | "crypto"
    | "other"
  >("paypal_goods_services");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [paymentDetails, setPaymentDetails] = useState("");
  const [notes, setNotes] = useState("");
  const [sdrInfo, setSdrInfo] = useState("");
  const [sdrAgentId, setSdrAgentId] = useState("");
  const [amountBought, setAmountBought] = useState("");
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [showBuyback, setShowBuyback] = useState(false);
  const [confirmingUnlink, setConfirmingUnlink] = useState(false);
  const [unlinkError, setUnlinkError] = useState<string | null>(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [pendingPaymentAction, setPendingPaymentAction] = useState<{
    id: string;
    status: "cleared" | "rejected";
  } | null>(null);

  // Approve (review -> cleared) or reject (review -> rejected) a flagged PayPal
  // payment. Either way it stays a context-only synced record that does not
  // count toward this PO's finance totals; rejecting keeps the record so the
  // sync won't re-create it.
  const handleReviewPayment = (
    paymentId: string,
    status: "cleared" | "rejected",
  ) => {
    setPendingPaymentAction({ id: paymentId, status });
    approvePaymentMutation.mutate(
      { paymentId, status },
      { onSettled: () => setPendingPaymentAction(null) },
    );
  };

  const isPaymentActionPending = (
    paymentId: string,
    status: "cleared" | "rejected",
  ) =>
    approvePaymentMutation.isPending &&
    pendingPaymentAction?.id === paymentId &&
    pendingPaymentAction?.status === status;

  useEffect(() => {
    setShowSaveSuccess(false);
    setShowBuyback(false);
    setConfirmingUnlink(false);
    setUnlinkError(null);
    setConfirmingCancel(false);
    setCancelReason("");
    setCancelError(null);
  }, [poId, open]);

  useEffect(() => {
    if (!purchaseOrder) {
      return;
    }

    if (purchaseOrder.status !== "voided") {
      setStatus(purchaseOrder.status);
    }
    setPaymentTiming(purchaseOrder.paymentTiming);
    setBuyPrice(String(purchaseOrder.buyPrice));
    setTaxCost(String(purchaseOrder.taxCost ?? 0));
    setNextPaymentDate(formatDateOnly(purchaseOrder.nextPaymentDate) || "");
    setOkToPay(Boolean(purchaseOrder.okToPay));
    setPaymentMethod(purchaseOrder.paymentMethod || "paypal");
    setPaymentNotes(purchaseOrder.paymentNotes || "");
    setPaymentDetails(purchaseOrder.paymentDetails || "");
    setNotes(purchaseOrder.notes || "");
    setSdrInfo(purchaseOrder.sdrInfo || "");
    setSdrAgentId(purchaseOrder.sdrAgentId || "");
    setAmountBought(String(purchaseOrder.amountBought));
  }, [purchaseOrder]);

  const handleSave = async () => {
    if (!purchaseOrder) {
      return;
    }

    const parsedAmountBought = Number(amountBought);
    const amountBoughtChanged =
      Number.isFinite(parsedAmountBought) &&
      parsedAmountBought !== purchaseOrder.amountBought;

    await updatePurchaseOrderMutation.mutateAsync({
      poId: purchaseOrder.id,
      data: {
        status,
        paymentTiming,
        buyPrice: Number(buyPrice),
        taxCost: Number(taxCost),
        amountBought: amountBoughtChanged ? parsedAmountBought : undefined,
        nextPaymentDate: nextPaymentDate || undefined,
        okToPay,
        paymentMethod,
        paymentNotes: paymentNotes.trim() || undefined,
        paymentDetails: paymentDetails.trim() || undefined,
        notes: notes.trim() || undefined,
        sdrAgentId: sdrAgentId || undefined,
      },
    });
    setShowSaveSuccess(true);
  };

  const isLinked = Boolean(purchaseOrder?.inventoryId);

  const handleCancel = async () => {
    if (!purchaseOrder || !cancelReason.trim()) {
      return;
    }
    setCancelError(null);
    try {
      await cancelPurchaseOrderMutation.mutateAsync({
        poId: purchaseOrder.id,
        reason: cancelReason.trim(),
      });
      setConfirmingCancel(false);
      setCancelReason("");
    } catch (error) {
      setCancelError(
        error instanceof Error
          ? error.message
          : "Failed to cancel the purchase order.",
      );
    }
  };

  const handleUnlink = async () => {
    if (!purchaseOrder) {
      return;
    }
    setUnlinkError(null);
    try {
      await unlinkPurchaseOrderMutation.mutateAsync({ poId: purchaseOrder.id });
      setConfirmingUnlink(false);
    } catch (error) {
      setUnlinkError(
        error instanceof Error
          ? error.message
          : "Failed to unlink the purchase order from inventory.",
      );
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full max-w-full overflow-y-auto p-0 md:max-w-2xl"
      >
        <div className="border-b px-6 py-5">
          <SheetHeader className="text-left">
            <SheetTitle>Purchase Order Details</SheetTitle>
            <SheetDescription>
              Review the linked inventory and purchase-side financials.
            </SheetDescription>
          </SheetHeader>
        </div>

        {!purchaseOrder ? (
          <div className="px-6 py-6 text-sm text-muted-foreground">
            {purchaseOrderQuery.isLoading
              ? "Loading..."
              : "Purchase order not found."}
          </div>
        ) : (
          <div className="space-y-6 px-6 py-6 text-sm">
            <WorkflowSummaryCard
              title={purchaseOrder.productName}
              description={
                purchaseOrder.vendorDisplayName ||
                purchaseOrder.vendorEmail ||
                "Unknown vendor"
              }
              status={getPurchaseOrderDisplayStatus(purchaseOrder)}
              health={health?.state}
              healthReason={health?.reason}
              metrics={[
                {
                  label: "Amount Bought",
                  value: `${formatNumber({ value: purchaseOrder.amountBought })} miles`,
                },
                {
                  label: "Base Amount",
                  value: formatCurrency({ value: purchaseOrder.buyPrice }),
                },
                {
                  label: "Tax",
                  value: formatCurrency({ value: purchaseOrder.taxCost }),
                },
                {
                  label: "Total",
                  value: formatCurrency({ value: totalAmount }),
                },
                {
                  label: "Cleared",
                  value: formatCurrency({ value: clearedAmount }),
                },
                {
                  label: "Outstanding",
                  value: formatCurrency({ value: remainingAmount }),
                },
              ]}
              footer={
                <div className="flex flex-wrap gap-2">
                  <Button asChild size="sm">
                    <Link
                      to={`/operations/finance?tab=payables&recordPoId=${purchaseOrder.id}`}
                    >
                      Record Payment
                    </Link>
                  </Button>
                  {isLinked ? (
                    <Button asChild size="sm" variant="outline">
                      <Link
                        to={buildInventoryWorkspacePath({
                          inventoryId: purchaseOrder.inventoryId,
                          purchaseOrderId: purchaseOrder.id,
                          focus: "purchase-orders",
                        })}
                        onClick={() => onOpenChange(false)}
                      >
                        Open Inventory Workspace
                      </Link>
                    </Button>
                  ) : null}
                  <SourceDealActions
                    sourceDealId={purchaseOrder.sourceDealId}
                  />
                </div>
              }
            />

            {showSaveSuccess ? (
              <div
                role="status"
                className="flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-700 dark:text-emerald-300"
              >
                <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
                <div className="flex-1">
                  Purchase order saved successfully.
                </div>
                <button
                  type="button"
                  aria-label="Dismiss notification"
                  onClick={() => setShowSaveSuccess(false)}
                  className="text-emerald-700/80 hover:text-emerald-700 dark:text-emerald-300/80 dark:hover:text-emerald-300"
                >
                  <X className="size-4" />
                </button>
              </div>
            ) : null}

            <div className="rounded-xl border p-4 space-y-3">
              <div className="font-medium">Order Details</div>
              <div className="flex flex-wrap gap-2">
                <Badge variant={isVoided ? "destructive" : "default"}>
                  {formatStatusLabel(
                    getPurchaseOrderDisplayStatus(purchaseOrder),
                  )}
                </Badge>
                <Badge variant="outline">
                  {formatStatusLabel(purchaseOrder.paymentStatus)}
                </Badge>
                <Badge variant="outline">
                  {formatStatusLabel(purchaseOrder.paymentTiming)}
                </Badge>
              </div>
              <div>Buy CPM: {purchaseOrder.buyCpm.toFixed(5)}</div>
              <div>Source: {purchaseOrder.source}</div>
              <div>
                Next Payment Date:{" "}
                {formatDateOnly(purchaseOrder.nextPaymentDate) || "-"}
              </div>
              <div>OK To Pay: {purchaseOrder.okToPay ? "Yes" : "No"}</div>
              <div>
                Payment Method: {formatStatusLabel(purchaseOrder.paymentMethod)}
              </div>
              <div>
                Tax Cost: {formatCurrency({ value: purchaseOrder.taxCost })}
              </div>
              <div>Total Amount: {formatCurrency({ value: totalAmount })}</div>
              {/* A cancellation zeroes the PO's miles by raising milesReturned to
                  amountBought, so labelling that as "bought back" would be a lie. */}
              {alreadyReturned > 0 && !isCancelled ? (
                <div>
                  Miles Bought Back: {formatNumber({ value: alreadyReturned })}{" "}
                  of {formatNumber({ value: purchaseOrder.amountBought })}
                </div>
              ) : null}
              {alreadyRefunded > 0 ? (
                <div>Refunded: {formatCurrency({ value: alreadyRefunded })}</div>
              ) : null}
              {purchaseOrder.voidedAt ? (
                <div>
                  {isCancelled ? "Cancelled" : "Voided"}{" "}
                  {formatTimestamp(purchaseOrder.voidedAt, "yyyy-MM-dd HH:mm") ||
                    "-"}
                </div>
              ) : null}
              {purchaseOrder.voidReason ? (
                <div>
                  {isCancelled ? "Cancellation Reason" : "Void Reason"}:{" "}
                  {purchaseOrder.voidReason}
                </div>
              ) : null}
              {purchaseOrder.sourceDealId ? (
                <div className="space-y-2">
                  <div>Source Deal</div>
                  <SourceDealActions
                    sourceDealId={purchaseOrder.sourceDealId}
                  />
                </div>
              ) : null}
              <div>
                Created{" "}
                {formatTimestamp(purchaseOrder.createdAt, "yyyy-MM-dd HH:mm") ||
                  "-"}
              </div>
              {purchaseOrder.paymentNotes ? (
                <div>Payment Notes: {purchaseOrder.paymentNotes}</div>
              ) : null}
              {purchaseOrder.paymentDetails ? (
                <div>Payment Details: {purchaseOrder.paymentDetails}</div>
              ) : null}
              {purchaseOrder.notes ? (
                <div>Notes: {purchaseOrder.notes}</div>
              ) : null}
              <div>
                SDR Connected:{" "}
                {purchaseOrder.sdrInfo &&
                purchaseOrder.sdrInfo.toLowerCase() !== "false"
                  ? "true"
                  : "false"}
              </div>
              {purchaseOrder.sdrAgentId ? (
                <div>
                  SDR Agent:{" "}
                  {users.find((u) => u.id === purchaseOrder.sdrAgentId)?.email?.split("@")[0]?.replace(/[._-]+/g, " ") ||
                    purchaseOrder.sdrAgentId}
                </div>
              ) : null}
            </div>

            {isVoided ? (
              <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-muted-foreground">
                {isCancelled
                  ? "This purchase order was cancelled and can no longer be edited. Its miles were returned to the linked inventory and any outstanding purchase invoices were voided."
                  : "This purchase order has been voided via buyback and can no longer be edited. See the buyback details above and the refund in the payments list below."}
              </div>
            ) : null}

            {!isVoided ? (
            <div className="rounded-xl border p-4 space-y-3">
              <div className="font-medium">Edit Purchase Order</div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>Status</Label>
                  <Select
                    value={status}
                    onValueChange={(value) => setStatus(value as typeof status)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="blocked">Blocked</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Payment Timing</Label>
                  <Select
                    value={paymentTiming}
                    onValueChange={(value) =>
                      setPaymentTiming(value as typeof paymentTiming)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pay_in_advance">
                        Paid in advance
                      </SelectItem>
                      <SelectItem value="pay_after">
                        Paid after flight
                      </SelectItem>
                      <SelectItem value="pay_once_booked">
                        Pay once booked
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1">
                  <Label>Amount Bought (miles)</Label>
                  <Input
                    type="number"
                    min={0}
                    step="1"
                    value={amountBought}
                    onChange={(event) => setAmountBought(event.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Buy Price</Label>
                  <Input
                    value={buyPrice}
                    onChange={(event) => setBuyPrice(event.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Tax Cost</Label>
                  <Input
                    value={taxCost}
                    onChange={(event) => setTaxCost(event.target.value)}
                  />
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Changing the quantity adjusts the linked inventory&apos;s total
                and available miles.
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1">
                  <Label>Next Payment Date</Label>
                  <Input
                    type="date"
                    value={nextPaymentDate}
                    onChange={(event) => setNextPaymentDate(event.target.value)}
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>Payment Method</Label>
                  <Select
                    value={paymentMethod}
                    onValueChange={(value) =>
                      setPaymentMethod(value as typeof paymentMethod)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paypal_goods_services">
                        PayPal - Goods and Services
                      </SelectItem>
                      <SelectItem value="paypal_friends_family">
                        PayPal - Friends and Family
                      </SelectItem>
                      <SelectItem value="paypal_goods_services_fees">
                        PayPal - Goods and Services + Fees
                      </SelectItem>
                      <SelectItem value="bank">Bank</SelectItem>
                      <SelectItem value="zelle">Zelle</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="crypto">Crypto</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>OK To Pay</Label>
                  <Select
                    value={okToPay ? "yes" : "no"}
                    onValueChange={(value) => setOkToPay(value === "yes")}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Payment Notes</Label>
                <Textarea
                  value={paymentNotes}
                  onChange={(event) => setPaymentNotes(event.target.value)}
                  placeholder="Payment notes"
                />
              </div>
              <div className="space-y-1">
                <Label>Payment Details</Label>
                <Textarea
                  value={paymentDetails}
                  onChange={(event) => setPaymentDetails(event.target.value)}
                  placeholder="Payment details"
                />
              </div>
              <div className="space-y-1">
                <Label>Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder="Operational notes"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>SDR Connected</Label>
                  <div className="flex h-10 items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground">
                    {sdrInfo && sdrInfo.toLowerCase() !== "false"
                      ? "true"
                      : "false"}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>SDR Agent</Label>
                  <Select
                    value={sdrAgentId}
                    onValueChange={setSdrAgentId}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select agent" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.email.split("@")[0]?.replace(/[._-]+/g, " ") || user.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={handleSave}
                  loading={updatePurchaseOrderMutation.isPending}
                  loadingText="Saving..."
                >
                  Save Purchase Order
                </Button>
              </div>
            </div>
            ) : null}

            {!isVoided ? (
              <div className="rounded-xl border p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium">Cancel Purchase Order</div>
                  {canCancel && !confirmingCancel ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-destructive hover:text-destructive"
                      onClick={() => {
                        setCancelError(null);
                        setConfirmingCancel(true);
                      }}
                    >
                      Cancel PO
                    </Button>
                  ) : null}
                </div>
                {!isLedgerLoaded ? (
                  <div className="text-xs text-muted-foreground">
                    Checking the payment ledger...
                  </div>
                ) : !canCancel ? (
                  <div className="text-xs text-muted-foreground">
                    {formatCurrency({ value: clearedAmount })} has already
                    cleared to the vendor on this purchase order, so it can&apos;t
                    simply be cancelled. Use Buy Back below to return the miles
                    and record the money coming back.
                  </div>
                ) : confirmingCancel ? (
                  <div className="space-y-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                    <div className="text-xs text-muted-foreground">
                      Cancelling closes this purchase order for good: its{" "}
                      {formatNumber({
                        value: Math.max(
                          purchaseOrder.amountBought - alreadyReturned,
                          0,
                        ),
                      })}{" "}
                      outstanding miles go back out of the linked inventory, any
                      unpaid purchase invoices on it are voided, and it drops out
                      of payment matching and the purchase reports. Nothing has
                      been paid to the vendor, so no refund is recorded. This
                      can&apos;t be undone.
                    </div>
                    <div className="space-y-1">
                      <Label>Reason</Label>
                      <Textarea
                        value={cancelReason}
                        onChange={(event) => setCancelReason(event.target.value)}
                        placeholder="Why is this purchase order being cancelled?"
                        rows={2}
                      />
                    </div>
                    {cancelError ? (
                      <div className="text-xs text-destructive">
                        {cancelError}
                      </div>
                    ) : null}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={!cancelReason.trim()}
                        loading={cancelPurchaseOrderMutation.isPending}
                        loadingText="Cancelling..."
                        onClick={handleCancel}
                      >
                        Confirm Cancellation
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={cancelPurchaseOrderMutation.isPending}
                        onClick={() => {
                          setConfirmingCancel(false);
                          setCancelReason("");
                          setCancelError(null);
                        }}
                      >
                        Keep Purchase Order
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">
                    Close out an order that never happened — the deal fell
                    through, the vendor never delivered, or the PO was raised in
                    error. Available while nothing has been paid to the vendor.
                  </div>
                )}
              </div>
            ) : null}

            {!isVoided ? (
              <div className="rounded-xl border p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium">Buy Back / Void</div>
                  {!showBuyback ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowBuyback(true)}
                    >
                      Record Buyback
                    </Button>
                  ) : null}
                </div>
                {showBuyback ? (
                  <BuyBackPurchaseOrderForm
                    poId={purchaseOrder.id}
                    amountBought={purchaseOrder.amountBought}
                    alreadyReturned={alreadyReturned}
                    alreadyRefunded={alreadyRefunded}
                    clearedOutgoing={clearedAmount}
                    inventoryAvailableMiles={inventoryQuery.data?.availableMiles}
                    defaultMethod={purchaseOrder.paymentMethod}
                    onSuccess={() => setShowBuyback(false)}
                  />
                ) : (
                  <div className="text-xs text-muted-foreground">
                    Record money the vendor paid back and return the miles to
                    them. Returning all remaining miles voids the PO.
                  </div>
                )}
              </div>
            ) : null}

            <div className="rounded-xl border p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium">Inventory Link</div>
                {isLinked && !confirmingUnlink ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-destructive hover:text-destructive"
                    onClick={() => {
                      setUnlinkError(null);
                      setConfirmingUnlink(true);
                    }}
                  >
                    Unlink from Inventory
                  </Button>
                ) : null}
              </div>
              {isLinked ? (
                <>
                  <div className="text-muted-foreground">
                    Linked to{" "}
                    <span className="font-medium text-foreground">
                      {inventoryQuery.data?.name || purchaseOrder.inventoryId}
                    </span>
                  </div>
                  {confirmingUnlink ? (
                    <div className="space-y-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                      <div className="text-xs text-muted-foreground">
                        Detaching this PO returns its{" "}
                        {formatNumber({
                          value: Math.max(
                            purchaseOrder.amountBought - alreadyReturned,
                            0,
                          ),
                        })}{" "}
                        outstanding miles from the linked inventory and removes it
                        from the inventory&apos;s purchase-order count. The PO
                        record is kept but will no longer belong to any inventory.
                        This cannot be done if those miles have already been sold.
                      </div>
                      {unlinkError ? (
                        <div className="text-xs text-destructive">
                          {unlinkError}
                        </div>
                      ) : null}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:text-destructive"
                          loading={unlinkPurchaseOrderMutation.isPending}
                          loadingText="Unlinking..."
                          onClick={handleUnlink}
                        >
                          Confirm Unlink
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={unlinkPurchaseOrderMutation.isPending}
                          onClick={() => {
                            setConfirmingUnlink(false);
                            setUnlinkError(null);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="text-muted-foreground">
                  This purchase order is not linked to any inventory.
                </div>
              )}
            </div>

            {isLinked ? (
            <div className="rounded-xl border p-4 space-y-3">
              <div className="font-medium">Inventory Credentials</div>
              {inventoryQuery.data ? (
                <>
                  <div>Name: {inventoryQuery.data.name}</div>
                  <div>
                    Owner: {inventoryQuery.data.accountOwnerName || "-"}
                  </div>
                  <div>Email: {inventoryQuery.data.accountEmail || "-"}</div>
                  <div>
                    Status:{" "}
                    {formatStatusLabel(inventoryQuery.data.accountStatus)}
                  </div>
                  <div>
                    2FA: {formatStatusLabel(inventoryQuery.data.twoFaStatus)}
                  </div>
                  <div>
                    Available Miles:{" "}
                    {formatNumber({
                      value: inventoryQuery.data.availableMiles,
                    })}
                  </div>
                </>
              ) : (
                <div className="text-muted-foreground">
                  {inventoryQuery.isLoading
                    ? "Loading..."
                    : "Inventory not found."}
                </div>
              )}
            </div>
            ) : null}

            <div className="rounded-xl border p-4 space-y-3">
              <div className="font-medium">Payments</div>
              {paymentsQuery.isLoading ? (
                <div className="text-muted-foreground">
                  Loading payment history...
                </div>
              ) : (paymentsQuery.data || []).length === 0 ? (
                <div className="text-muted-foreground">
                  No payments recorded yet.
                </div>
              ) : (
                (paymentsQuery.data || []).map((payment) => {
                  const isIncoming = payment.direction === "incoming";
                  return (
                    <div
                      key={payment.id}
                      className="flex items-center justify-between rounded-lg border bg-muted/20 p-3"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <div
                            className={
                              isIncoming
                                ? "font-medium text-emerald-600 dark:text-emerald-400"
                                : "font-medium"
                            }
                          >
                            {isIncoming ? "+" : ""}
                            {formatCurrency({ value: payment.amount })}
                            {isIncoming ? " refund" : ""}
                          </div>
                          {payment.source ? (
                            <Badge variant="outline" className="text-[10px]">
                              Context only · not counted
                            </Badge>
                          ) : null}
                          {payment.status === "review" ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge
                                    variant="outline"
                                    className="cursor-help border-amber-500 text-[10px] text-amber-600 dark:text-amber-400"
                                  >
                                    For review
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  {payment.mismatchPct !== undefined
                                    ? `Amount differs from this purchase order's total by ${(payment.mismatchPct * 100).toFixed(1)}% (within the 15% review threshold). Approve to confirm the link or reject it.`
                                    : "Amount differs from this purchase order's total (within the 15% review threshold). Approve to confirm the link or reject it."}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : null}
                          {payment.status === "rejected" ? (
                            <Badge
                              variant="outline"
                              className="border-destructive text-[10px] text-destructive"
                            >
                              Rejected
                            </Badge>
                          ) : null}
                        </div>
                        <div className="text-muted-foreground">
                          {formatStatusLabel(payment.status)} via{" "}
                          {formatStatusLabel(payment.method)}
                        </div>
                        {payment.referenceId ? (
                          <div className="break-all text-xs text-muted-foreground">
                            Ref: {payment.referenceId}
                          </div>
                        ) : null}
                        {payment.notes ? (
                          <div className="text-xs text-muted-foreground">
                            {payment.notes}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className="text-right text-muted-foreground">
                          {formatTimestamp(
                            payment.paidAt || payment.createdAt,
                            "yyyy-MM-dd HH:mm",
                          ) || "-"}
                        </div>
                        {payment.status === "review" ? (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              loading={isPaymentActionPending(
                                payment.id,
                                "cleared",
                              )}
                              loadingText="Approving..."
                              onClick={() =>
                                handleReviewPayment(payment.id, "cleared")
                              }
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive hover:text-destructive"
                              loading={isPaymentActionPending(
                                payment.id,
                                "rejected",
                              )}
                              loadingText="Rejecting..."
                              onClick={() =>
                                handleReviewPayment(payment.id, "rejected")
                              }
                            >
                              Reject
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
